/**
 * Route/UI access helpers for the RBAC cutover (RBAC plan §3.2/§3.6).
 *
 * Every human session is a shared Cognito session whose canonical `Cyngn-*`
 * roles and companies come from the ID token (`useAuth()`). The legacy
 * `currentUser` role fallbacks are gone (DEV-6053): a session with no canonical
 * role has no access these hooks can grant.
 *
 * The token carries the pool-wide *union* of a user's roles, so an admin of
 * company A who is only a `Cyngn-Operator` of company B still has `Cyngn-Admin`
 * in `cognito:groups` while operating as B (DEV-6432). Admin access is therefore
 * resolved against the active company: `useAuth().activeCompanyRole`, the role
 * the session holds there per its own memberships on `users/me/`, which is the
 * authorization source of truth (`UserCompanyAssignment`).
 *
 * Like everything in `useAuth()`, the answers are **UX-only** — the ID token is
 * decoded without signature verification, so they decide what to render, never
 * what is allowed (DEV-4482; cloud-core enforces).
 */
import { useFlags } from "launchdarkly-react-client-sdk";

import { FEATURE_FLAGS } from "../../constants/featureFlags";

import { useAuth } from "./AuthProvider";

/**
 * Whether the session is `Cyngn-Admin` of the company it is operating as.
 *
 * Decided by `activeCompanyRole` when it is known. While it is not — the
 * memberships are not loaded yet, the user has none (a legacy account the
 * backfill has not reached, whose only role is the token's), or no company is
 * active — the token's role decides, as before. A known role that is not
 * `Cyngn-Admin` (or no membership in the active company at all) denies,
 * whatever the token's union says.
 */
export const useIsAdminOfActiveCompany = (): boolean => {
  const { hasRole, activeCompanyRole } = useAuth();

  if (!hasRole("Cyngn-Admin")) {
    return false;
  }
  if (activeCompanyRole === undefined) {
    return true;
  }

  return activeCompanyRole === "Cyngn-Admin";
};

/**
 * Whether the session is exactly `Cyngn-Operator` of the company it is
 * operating as — the canonical successor of the legacy `Mission Operator`
 * (DEV-6457), which the missions UI treats as a *restriction* (no emergency
 * stop), not a grant. It is therefore literal membership, never the ladder:
 * an admin is not an operator here.
 *
 * Decided by `activeCompanyRole` when it is known; while it is not, by whether
 * the token's groups carry `Cyngn-Operator` at all.
 */
export const useIsOperatorOfActiveCompany = (): boolean => {
  const { roles, activeCompanyRole } = useAuth();

  if (activeCompanyRole === undefined) {
    return roles.includes("Cyngn-Operator");
  }

  return activeCompanyRole === "Cyngn-Operator";
};

/**
 * Whether the session may list `GET /api/v1/roles/`, which cloud-core floors
 * at `Cyngn-Admin` on the token's pool-wide groups (DEV-4481) — so this follows
 * `hasRole`, not the active company. A canonical non-admin (`Cyngn-Operator`,
 * `Cyngn-Viewer`) would only collect a 403, so the roles list is not fetched
 * for it (DEV-6316). A session with no canonical role at all is a legacy
 * account the backfill has not reached: it keeps the pre-RBAC fetch so the
 * legacy `User.role` fallbacks (`getIsAdmin`, `getIsMissionOperator`) still
 * resolve while RBAC runs in dual mode.
 */
export const useCanListRoles = (): boolean => {
  const { roles, hasRole } = useAuth();

  return hasRole("Cyngn-Admin") || roles.length === 0;
};

/** Admin-level access: `Cyngn-Admin` in the active company, with no ladder above it. */
export const useHasAdminAccess = (): boolean => useIsAdminOfActiveCompany();

/**
 * Whether this session uses the Admin Console at `/admin` (DEV-3767).
 *
 * True only for a session that is `Cyngn-Admin` of the active company with
 * `ADMIN_CONSOLE_ENABLED` on: the console's API is locked to the admins of the
 * company named by `X-Active-Company-ID`.
 */
export const useAdminConsoleAccess = (): boolean => {
  const isAdmin = useIsAdminOfActiveCompany();
  const flags = useFlags();

  return !!flags[FEATURE_FLAGS.ADMIN_CONSOLE_ENABLED] && isAdmin;
};
